import React, { useState, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getOrCreateConversation } from '@/lib/messages';

interface MinimizedChatEntry {
  conversationId: string;
  company: any;
  minimizedAt: number; // timestamp for LRU behavior
}

interface ChatState {
  isOpen: boolean;
  isMinimized: boolean; // retained (represents current chat minimized)
  company: any | null;
  isSending: boolean;
  messages: any[];
  conversationId: string | null;
  messagesLoaded: boolean;
  minimized: MinimizedChatEntry[]; // multiple minimized chats (newest at index 0)
  isOpeningFromBubble: boolean; // animation flag when opening from bubble
}

interface ChatContextType {
  chatState: ChatState;
  openChat: (company: any, options?: { fromBubble?: boolean }) => void;
  closeChat: () => void;
  minimizeChat: () => void; // animated minimize handled in UI, this just commits state
  sendMessage: (message: string) => Promise<void>;
  loadMessages: (conversationId?: string) => Promise<void>;
  closeMinimizedChat: (conversationId: string) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  
  const [chatState, setChatState] = useState<ChatState>({
    isOpen: false,
    isMinimized: false,
    company: null,
    isSending: false,
    messages: [],
    conversationId: null,
    messagesLoaded: false,
    minimized: [],
    isOpeningFromBubble: false,
  });

  const openChat = async (company: any, options?: { fromBubble?: boolean }) => {
    if (!user || !userProfile || (userProfile.role !== 'homeowner' && userProfile.role !== 'contractor')) {
      toast({
        title: 'Erro',
        description: 'Apenas proprietários e contratantes podem usar o chat.',
        variant: 'destructive',
      });
      return;
    }

    // If another chat is open for a different company, silently minimize it (no animation requirement here)
    setChatState(prev => {
      if (prev.isOpen && prev.company && prev.company.id !== company.id && prev.conversationId) {
        // Remove existing if already among minimized then re-add as newest on top
        const filtered = prev.minimized.filter(m => m.conversationId !== prev.conversationId);
        const updated = [{
          conversationId: prev.conversationId,
          company: prev.company,
          minimizedAt: Date.now(),
        }, ...filtered];
        while (updated.length > 5) updated.pop();
        return { ...prev, minimized: updated, isOpen: false, isMinimized: true };
      }
      return prev;
    });

    // Try to find existing conversation; do NOT create one yet (avoid ghost chats)
    try {
      const { data: conv, error } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(participant1_id.eq.${user.id},participant2_id.eq.${company.id}),and(participant1_id.eq.${company.id},participant2_id.eq.${user.id})`)
        .maybeSingle();

      const existingConversationId = error ? null : conv?.id || null;

      setChatState(prev => ({
        ...prev,
        isOpen: true,
        isMinimized: false,
        company,
        isSending: false,
        messages: [],
        conversationId: existingConversationId,
        messagesLoaded: existingConversationId ? false : true,
        // Remove any minimized entry for this chat, by conversationId or by company (temp IDs)
        minimized: prev.minimized.filter(m => (
          (existingConversationId ? m.conversationId !== existingConversationId : true) &&
          m.company?.id !== company.id
        )),
        isOpeningFromBubble: !!options?.fromBubble,
      }));

      if (existingConversationId) {
        await loadMessages(existingConversationId);
      }
    } catch (e) {
      // Open chat anyway without messages; conversation will be created on first send
      setChatState(prev => ({
        ...prev,
        isOpen: true,
        isMinimized: false,
        company,
        isSending: false,
        messages: [],
        conversationId: null,
        messagesLoaded: true,
      }));
    }
  };

  const loadMessages = async (conversationId?: string) => {
    if (!conversationId && !chatState.conversationId) return;
    
    const id = conversationId || chatState.conversationId!;
    
    try {
      const { data: messages, error } = await supabase
        .from('messages')
        .select(`*, sender:sender_id(display_name), receiver:receiver_id(display_name)`) 
        .eq('conversation_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setChatState(prev => ({ ...prev, messages: messages || [], messagesLoaded: true }));
    } catch (error) {
      console.error('Error loading messages:', error);
      setChatState(prev => ({ ...prev, messagesLoaded: true }));
    }
  };

  const closeChat = () => {
    setChatState(prev => ({
      ...prev,
      isOpen: false,
      isMinimized: false,
      messages: [],
      conversationId: null,
      messagesLoaded: false,
    }));
  };

  // Called AFTER animation completes (UI controls timing)
  const minimizeChat = () => {
    setChatState(prev => {
      if (!prev.company) return prev;
      const idForBubble = prev.conversationId || `temp:${prev.company.id}`;

      const filtered = prev.minimized.filter(m => m.conversationId !== idForBubble && m.company?.id !== prev.company?.id);
      const updated = [{
        conversationId: idForBubble,
        company: prev.company,
        minimizedAt: Date.now(),
      }, ...filtered];

      // Do NOT prune here; allow overflow to 6 for staged animation (UI will prune after animation)

      return {
        ...prev,
        isOpen: false,
        isMinimized: true,
        minimized: updated,
      };
    });
  };

  const closeMinimizedChat = (conversationId: string) => {
    setChatState(prev => ({
      ...prev,
      minimized: prev.minimized.filter(m => m.conversationId !== conversationId),
    }));
  };

  const sendMessage = async (messageContent: string) => {
    if (!user || !chatState.company) return;

    setChatState(prev => ({ ...prev, isSending: true }));
    
    try {
      let conversationId = chatState.conversationId;
      if (!conversationId) {
        // Create/find conversation on first send
        conversationId = await getOrCreateConversation(user.id, chatState.company.id);
        setChatState(prev => ({ ...prev, conversationId }));
      }

      const { data: newMessage, error } = await supabase.from('messages').insert({
        conversation_id: conversationId,
        sender_id: user.id,
        receiver_id: chatState.company.id,
        content: messageContent,
        read: false
      }).select().single();

      if (error) throw error;

      setChatState(prev => ({ 
        ...prev, 
        messages: [...prev.messages, newMessage]
      }));

    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao enviar mensagem. Tente novamente.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setChatState(prev => ({ ...prev, isSending: false }));
    }
  };

  return (
    <ChatContext.Provider value={{
      chatState,
      openChat,
      closeChat,
      minimizeChat,
      sendMessage,
      loadMessages,
      closeMinimizedChat,
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
